"use client";

import {
  QueryCache,
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState, type ReactNode } from "react";

import { isUnauthorizedError } from "@/lib/api-client";
import { logoutAndRedirect } from "@/lib/session/client";

function createQueryClient() {
  const handleError = (error: unknown) => {
    if (!isUnauthorizedError(error)) {
      return;
    }
    // The /preview/* routes seed React Query with mock data and intentionally
    // run under a dev token that the upstream API rejects. Skip the auto
    // logout-redirect there so the preview stays stable when a stray query
    // (impact, audit, etc.) escapes the seed and 401s.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/preview/")) {
      return;
    }
    logoutAndRedirect();
  };

  return new QueryClient({
    queryCache: new QueryCache({
      onError: handleError,
    }),
    mutationCache: new MutationCache({
      onError: handleError,
    }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (isUnauthorizedError(error)) {
            return false;
          }
          return failureCount < 2;
        },
      },
    },
  });
}

export function QueryProviders({ children }: { children: ReactNode }) {
  const [client] = useState(() => createQueryClient());

  return (
    <QueryClientProvider client={client}>
      {children}
      {process.env.NODE_ENV !== "production" ? (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom"
          buttonPosition="bottom-right"
        />
      ) : null}
    </QueryClientProvider>
  );
}
